import { useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { ArrowUp, Loader2, Paperclip, Square, X } from "lucide-react";
import type { FileInfo } from "../../api";
import { FileChip } from "./MessageItem";

export interface Attachment {
  localId: string;
  name: string;
  status: "uploading" | "ready" | "error";
  file?: FileInfo; // Agent Studio upload
  error?: string;
  meta?: string; // e.g. "1,204 rows"
}

export const SLASH_CMDS = [
  { cmd: "/run-experiment", desc: "Design a Synthetic Control study", icon: "🧪" },
  { cmd: "/run-mmm", desc: "Run a Marketing Mix Model job", icon: "📈" },
  { cmd: "/run-bid-optimizer", desc: "Optimise bids across segments", icon: "🎯" },
];

interface Props {
  mode: "studio" | "assistant";
  initialText?: string; // e.g. a ?q= prefill from Home or Cmd+K
  attachments: Attachment[];
  streaming: boolean;
  controls?: ReactNode; // pickers next to the attach button (agent, model)
  onAttach: (files: File[]) => void;
  onRemoveAttachment: (localId: string) => void;
  onSend: (text: string) => void;
  onStop: () => void;
}

export default function Composer({
  mode, initialText, attachments, streaming, controls, onAttach, onRemoveAttachment, onSend, onStop,
}: Props) {
  const [text, setText] = useState(initialText ?? "");
  const [dragging, setDragging] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const assistant = mode === "assistant";
  const uploading = attachments.some((a) => a.status === "uploading");
  // Study Assistant job setup accepts an empty message as "use the default".
  const canSend = (assistant || text.trim().length > 0) && !uploading && !streaming;

  const slashFilter = assistant && text.startsWith("/") && !text.includes(" ") ? text.slice(1).toLowerCase() : null;
  const slashMatches = slashFilter === null ? [] : SLASH_CMDS.filter((c) => c.cmd.slice(1).includes(slashFilter));

  const resize = () => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  };

  const submit = () => {
    if (!canSend) return;
    onSend(text.trim());
    setText("");
    if (textRef.current) textRef.current.style.height = "auto";
  };

  const applySlash = (cmd: string) => {
    setText(`${cmd} `);
    setSlashIndex(0);
    textRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashMatches.length > 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setSlashIndex((i) => (i + step + slashMatches.length) % slashMatches.length);
        return;
      }
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault();
        applySlash(slashMatches[Math.min(slashIndex, slashMatches.length - 1)].cmd);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) onAttach(Array.from(e.dataTransfer.files));
  };

  return (
    <div className="relative">
      {slashMatches.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 z-40 mb-2 overflow-hidden rounded-card border border-border-default-t bg-elevated shadow-lg">
          <div className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-tertiary-t">Commands</div>
          {slashMatches.map((c, i) => (
            <button
              key={c.cmd}
              type="button"
              onClick={() => applySlash(c.cmd)}
              className={`flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-hover-bg ${i === slashIndex ? "bg-hover-bg" : ""}`}
            >
              <span className="text-base">{c.icon}</span>
              <span>
                <span className="block text-[13px] font-semibold text-primary-t">{c.cmd}</span>
                <span className="block text-xs text-tertiary-t">{c.desc}</span>
              </span>
            </button>
          ))}
          <div className="border-t border-border-subtle-t px-3 py-1.5 text-[10.5px] text-tertiary-t">
            Tab or Enter to select
          </div>
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`rounded-2xl border bg-surface transition ${dragging ? "border-adrsta-blue" : "border-border-default-t"}`}
      >
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 pt-3">
            {attachments.map((a) => (
              <span key={a.localId} className="relative inline-flex items-center">
                {a.status === "uploading" ? (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-border-default-t px-2 py-1 text-xs text-tertiary-t">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> {a.name}
                  </span>
                ) : a.status === "error" ? (
                  <span
                    title={a.error}
                    className="inline-flex max-w-[320px] items-center gap-1.5 truncate rounded-lg border px-2 py-1 text-xs"
                    style={{ borderColor: "var(--error)", color: "var(--error)" }}
                  >
                    {a.name}: {a.error}
                  </span>
                ) : a.file ? (
                  <FileChip file={a.file} />
                ) : (
                  <FileChip name={a.meta ? `${a.name} · ${a.meta}` : a.name} />
                )}
                <button
                  onClick={() => onRemoveAttachment(a.localId)}
                  className="ml-1 rounded-full p-0.5 text-tertiary-t hover:bg-hover-bg hover:text-primary-t"
                  title="Remove"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <textarea
          ref={textRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setSlashIndex(0);
            resize();
          }}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={
            dragging
              ? "Drop files to attach"
              : assistant
                ? "Ask about your MMM, experiments or bids — or type / for commands…"
                : "Ask a question, or attach a CSV to analyze…"
          }
          className="block w-full resize-none border-0 bg-transparent px-4 pt-3 text-[13.5px] text-primary-t placeholder:text-tertiary-t focus:outline-none focus:ring-0"
        />

        <div className="flex items-center gap-1 px-3 pb-3 pt-1">
          <button
            onClick={() => inputRef.current?.click()}
            title={assistant ? "Attach a CSV to set up a job" : "Attach files (CSV, Excel, PDF, TXT, MD, JSON)"}
            className="rounded-lg p-2 text-tertiary-t hover:bg-hover-bg hover:text-adrsta-blue"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple={!assistant}
            accept={assistant ? ".csv" : ".csv,.xlsx,.pdf,.txt,.md,.json"}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) onAttach(Array.from(e.target.files));
              e.target.value = "";
            }}
          />
          {controls}
          <span className="ml-auto" />
          {streaming ? (
            <button
              onClick={onStop}
              title="Stop"
              className="rounded-lg p-2 hover:opacity-80"
              style={{ background: "var(--text-primary)", color: "var(--bg-base)" }}
            >
              <Square className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!canSend}
              title="Send"
              className="rounded-lg bg-adrsta-blue p-2 text-white hover:opacity-90 disabled:opacity-40"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
